// src/components/AccountSwitcher.tsx
import { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ExpandMore,
  Business,
  Check,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const AccountSwitcher = () => {
  const { accounts, currentAccountId, setCurrentAccountId } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const currentAccount = accounts.find((a) => a.id === currentAccountId);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAccountSelect = (accountId: string) => {
    if (accountId !== currentAccountId) {
      // Save to localStorage BEFORE navigation to avoid race condition
      localStorage.setItem("current_account_id", accountId);
      // Redirect to /cases to ensure user lands on valid page for new account
      window.location.href = "/cases";
    }
    handleClose();
  };

  // Don't render if user only has one account
  if (accounts.length <= 1) {
    return null;
  }

  return (
    <>
      <Button
        startIcon={<Business />}
        endIcon={<ExpandMore />}
        onClick={handleClick}
        sx={{
          textTransform: 'none',
          fontWeight: 'medium',
          color: 'text.primary',
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
          },
          borderRadius: 1,
          px: 2,
          mr: 1,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <Typography variant="caption" sx={{ lineHeight: 1, opacity: 0.7 }}>
            Account
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.2, fontWeight: 'medium' }}>
            {currentAccount?.name || 'Select Account'}
          </Typography>
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            minWidth: 220,
            mt: 1,
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Switch Account
          </Typography>
        </Box>
        <Divider />
        {accounts.map((account) => {
          const isSelected = account.id === currentAccountId;
          const isDemo = account.name.toLowerCase().includes('demo');

          return (
            <MenuItem
              key={account.id}
              onClick={() => handleAccountSelect(account.id)}
              selected={isSelected}
              sx={{
                py: 1.5,
              }}
            >
              <ListItemIcon>
                {isSelected ? (
                  <Check color="primary" />
                ) : (
                  <Business sx={{ opacity: 0.5 }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {account.name}
                    {isDemo && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          backgroundColor: 'info.main',
                          color: 'white',
                          px: 0.75,
                          py: 0.25,
                          borderRadius: 0.5,
                          fontSize: '0.65rem',
                          fontWeight: 'bold',
                        }}
                      >
                        DEMO
                      </Typography>
                    )}
                  </Box>
                }
                secondary={account.domain || undefined}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default AccountSwitcher;
